/*
 * Bwè Manjé is a restaurant table booking application on the Android Platform.
 *
 * Copyright (C) 2020-2025 by Frédéric-Charles Barthéléry.
 *
 * This file is part of Bwè Manjé.
 */
package com.geekorum.rdv.bwemanje.accountdeletion

import androidx.compose.runtime.*
import com.geekorum.rdv.bwemanje.accountdeletion.compose.viewmodel.viewModel
import firebase.app.FirebaseApp
import firebase.auth
import firebase.auth.Auth
import firebase.auth.deleteUser
import js.firebaseui.AuthUI
import js.firebaseui.Config
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.js.jso
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.required
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.dom.*

class AccountDeletionViewModel(
    val authUi: AuthUI,
    val auth: Auth
) {
    fun signOut() {
        auth.signOut()
    }

    val authUiConfig: Config = jso {
//        signInOptions = arrayOf(GoogleAuthProvider.PROVIDER_ID)
        signInOptions = arrayOf("google.com")
        signInSuccessUrl = "/"
        tosUrl = "/terms-of-service.html"
        privacyPolicyUrl = "/privacy-policy.html"
    }

    val currentUser = auth.currentUserFlow()
    val isPendingRedirect: Boolean
        get() = authUi.isPendingRedirect()

    suspend fun deleteAccount() {
        auth.currentUser?.let {
            deleteUser(it)
        }?.await()
    }
}


@Composable
fun AccountDeletionPage(firebaseApp: FirebaseApp) {
    val viewModel = viewModel {
        AccountDeletionViewModel(
            authUi = AuthUI.getInstance()?.apply { reset() }
                ?: AuthUI(firebaseApp.auth()),
            auth = firebaseApp.auth()
        )
    }
    Scaffold {
        var navState by remember { mutableStateOf(NavigationState.INTRODUCTION) }
        if (navState == NavigationState.INTRODUCTION && viewModel.isPendingRedirect) {
            SignInSection(viewModel.authUi,
                config = viewModel.authUiConfig,
                onSignInSuccess = {
                    navState = NavigationState.CONFIRM
                })
        } else {
            when (navState) {
                NavigationState.INTRODUCTION -> Introduction(onNextClick = {
                    navState = NavigationState.SIGNIN
                })

                NavigationState.SIGNIN -> SignIn(viewModel, onSignInSuccess = {
                    navState = NavigationState.CONFIRM
                })

                NavigationState.CONFIRM -> ConfirmDeletion(viewModel,
                    onCancelClick = {
                        viewModel.signOut()
                        navState = NavigationState.INTRODUCTION
                    },
                    onAccountDeletionFails = {
                        viewModel.signOut()
                        navState = NavigationState.SIGNIN
                    },
                    onAccountDeleted = {
                        navState = NavigationState.COMPLETED
                    }
                )
                NavigationState.COMPLETED -> AccountDeleted()
            }
        }
    }
}

@Composable
private fun Introduction(onNextClick: () -> Unit) {
    P {
        Text("Cette page vous permet de supprimer votre compte sur la plateforme. " +
                "Cette action est irréversible et entraînera la suppression de toutes vos données " +
                "personnelles.")
    }

    H3 {
        Text("Attention")
    }
    P {
        Text("La suppression de votre compte est définitive. Une fois votre compte supprimé, " +
                "vous ne pourrez plus vous y connecter. Vous ne pourrez pas non plus récupérer " +
                "vos données personnelles.")
    }

    A(href = "#", attrs = {
        onClick {
            onNextClick()
        }
    }) {
        Text("Je veux supprimer mon compte")
    }
}

@Composable
fun SignIn(viewModel: AccountDeletionViewModel, onSignInSuccess: () -> Unit) {
    P {
        Text("Nous sommes désolés de voir que vous souhaitez quitter Bwè Manjé. " +
                "Commencez par vous connectez a votre compte et confirmez votre demande."
        )
    }
    val currentUser by viewModel.currentUser.collectAsState(null)
    LaunchedEffect(currentUser) {
        if (currentUser != null) {
            println("current user not null call onSignInSUccess")
            onSignInSuccess()
        }
    }

    SignInSection(viewModel.authUi,
        config = viewModel.authUiConfig,
        onSignInSuccess = {
            println("signin success")
            onSignInSuccess()
        })
}

@Composable
private fun ConfirmDeletion(
    viewModel: AccountDeletionViewModel, onCancelClick: () -> Unit,
    onAccountDeletionFails: () -> Unit,
    onAccountDeleted: () -> Unit
) {
    val currentUser by viewModel.currentUser.collectAsState(null)
    P {
        Text("Nous sommes désolés de voir que vous souhaitez quitter Bwè Manjé. " +
                "Veuillez cocher cette case pour confirmer votre demande de suppression."
        )
    }

    var confirmChecked by remember { mutableStateOf(false) }
    Label(attrs = {
        style {
            marginBottom(6.cssRem)
        }
    }) {
        CheckboxInput(confirmChecked) {
            required()
            onChange { confirmChecked = !confirmChecked }
        }
        Text(" Je souhaite supprimer le compte ${currentUser?.email}")
    }

    Button(attrs = {
        onClick {
            onCancelClick()
        }
    }) {
        Text("Annuler")
    }

    val coroutineScope = rememberCoroutineScope()
    Button(attrs = {
        if (!confirmChecked) {
            disabled()
        } else {
            classes("button-primary")
        }
        onClick {
            coroutineScope.launch {
                try {
                    viewModel.deleteAccount()
                    onAccountDeleted()
                } catch (e: Throwable) {
                    console.error("Unable to delete account $e")
                    onAccountDeletionFails()
                }
            }
        }
    }) {
        Text("Supprimer mon compte")
    }
}

@Composable
private fun AccountDeleted() {
    P {
        Text("Votre compte a été supprimé avec succès.")
        Br()
        Text("Nous apprécions votre participation à la plateforme et nous espérons que vous reviendrez bientôt. " +
                "Si vous avez des questions ou des commentaires, veuillez nous contacter.")
        Br()
        Text("Vos données personnelles ont été supprimées.")
    }

    A(href = "/") {
        Text("Retour à l'accueil")
    }
}

private enum class NavigationState {
    INTRODUCTION, SIGNIN, CONFIRM, COMPLETED
}

@Composable
private fun Scaffold(content: @Composable () -> Unit) {
    H2({
        style {
            textAlign("center")
        }
    }) {
        Text(("Suppression de compte"))
    }
    content()
}
